
import { z } from 'zod';

export const ActivityCategorySchema = z.enum(['swim', 'walk', 'food', 'lodging', 'flight', 'train', 'car', 'other']).describe("The category of the activity.");
export type ActivityCategory = z.infer<typeof ActivityCategorySchema>;

// AI-generated itinerary types (copied from generate-personalized-itinerary.ts to avoid importing server-side code)
const ItineraryDaySchema = z.object({
  day: z.number().describe('The day number of the itinerary, starting from 1.'),
  date: z.string().describe('The specific date for this day of the itinerary in YYYY-MM-DD format.'),
  title: z.string().describe("A short, catchy title for the day's plan (e.g., 'Mystical Lochs and Forest Walks')."),
  activities: z.array(z.object({
    time: z.string().describe('A time of day for the activity, e.g., "Morning", "Afternoon", "Evening".'),
    description: z.string().describe('A detailed, engaging description of the activity, including safety tips.'),
    locationName: z.string().describe('The specific name of the location for the activity (e.g., "Fairy Pools").'),
    locationSlug: z.string().describe('A URL-friendly slug for the location name (e.g., "fairy-pools").'),
    category: ActivityCategorySchema,
    coordinates: z.object({
      lat: z.number().describe('The latitude of the location.'),
      lng: z.number().describe('The longitude of the location.'),
    }).describe('The geographic coordinates of the location.'),
  })).describe('An array of activities for the day.'),
});

const PersonalizedItineraryOutputSchema = z.object({
  title: z.string().describe('A creative and exciting title for the entire wild swimming holiday.'),
  days: z.array(ItineraryDaySchema).describe('An array of daily itinerary plans.'),
});

export type PersonalizedItineraryOutput = z.infer<typeof PersonalizedItineraryOutputSchema>;


export type Loch = {
  id: string;
  "Loch Name": string;
  "County/Region": string;
  Latitude: number;
  Longitude: number;
  slug: string; // This will be the same as id
  name: string; // This will be the same as "Loch Name"
  region: string; // This will be the same as "County/Region"
  description: string;
  details: string;
  access: string;
  water_quality: 'Excellent' | 'Good' | 'Sufficient' | 'Poor';
  rating: number; // 1-5
  imageId: string;
};


export type SwimmingLocation = {
  id: string;
  name: string;
  slug: string;
  region: string;
  latitude: number;
  longitude: number;
  description: string;
  details: string;
  access: string;
  water_quality: 'Excellent' | 'Good' | 'Sufficient' | 'Poor';
  rating: number; // 1-5
  imageId: string;
};

export type UserProfile = {
  name: string;
  avatarImageId: string;
  favoriteSpots: string[];
  loggedSwims: LoggedSwim[];
};

export type LoggedSwim = {
  locationId: string;
  locationName: string;
  locationRegion: string;
  locationImageId: string;
  date: string; // ISO 8601 format
  notes: string;
}

export type ToDo = {
  id: string;
  text: string;
  completed: boolean;
}

export type Note = {
  id:string;
  content: string;
}

// This now includes the date field for each day
export type ItineraryDay = PersonalizedItineraryOutput['days'][0] & {
  notes?: Note[];
  // Overwrite activities to be more specific
  activities: (PersonalizedItineraryOutput['days'][0]['activities'][0] & {
    // Shared optional fields
    bookingReference?: string;
    
    // Flight specific
    flightNumber?: string;
    airline?: string;
    departureAirport?: string;
    arrivalAirport?: string;
    departureTime?: string;
    arrivalTime?: string;
    
    // Train specific
    departureStation?: string;
    arrivalStation?: string;
    trainOperator?: string;

    // Car rental specific
    rentalCompany?: string;
    pickupLocation?: string;
    dropoffLocation?: string;
    pickupTime?: string;
    dropoffTime?: string;

    // Lodging specific
    checkInTime?: string;
    checkOutTime?: string;
  })[];
};

export interface Itinerary {
  id: string;
  userProfileId: string;
  name: string;
  startDate: string; // The official start date of the whole trip
  endDate: string; // The official end date of the whole trip
  preferences: string;
  skillLevel: 'beginner' | 'intermediate' | 'advanced';
  duration: number;
  location: string;
  generatedItinerary: Omit<PersonalizedItineraryOutput, 'days'> & { days: ItineraryDay[] };
  notes?: Note[];
  todos?: ToDo[];
}
